<template>
    <div class="contact-list-container">
        <table class="contact-table">
            <thead>
                <tr>
                    <th />
                    <th class="item-contact">
                        {{ $t('contactSelection.table.contact') }}
                        <ReglePastille cle="demandeIntervention.contactSurSite" />
                    </th>
                    <th>
                        {{ $t('contact.form.phone.mobile') }}
                        <ReglePastille cle="demandeIntervention.telephoneContactSurSite" />
                    </th>
                    <th>
                        {{ $t('contact.form.phone.fixe') }}
                    </th>
                    <th class="buttons-container" />
                </tr>
            </thead>
            <tbody>
                <tr class="item" v-on:click="toggleSelect(contact)" v-for="contact in contacts" :key="contact.codeSite">
                    <td class="select">
                        <div class="select-border">
                            <RadioBox class="radio" :selected="contact.telephoneMobile === selectedContact.telephoneMobile && contact.telephoneFixe === selectedContact.telephoneFixe" />
                        </div>
                    </td>
                    <td class="item-contact" :class="{'no-valid': !contact.firstname || !contact.lastname}">
                        <div class="item-name">
                            <span>
                                {{ `${contact.firstname} ${contact.lastname.toUpperCase()}` }}
                            </span>
                        </div>
                    </td>
                    <td class="item-tel-mobile" :class="{'no-valid': !telValidation(contact.telephoneMobile)}">
                        <div>
                            <span v-if="contact.telephoneMobile">{{ contact.telephoneMobile }}</span>
                        </div>
                    </td>
                    <td class="item-tel-fixe" :class="{'no-valid': !telValidation(contact.telephoneFixe)}">
                        <div>
                            <span v-if="contact.telephoneFixe">{{ contact.telephoneFixe }}</span>
                        </div>
                    </td>
                    <td class="buttons-container">
                        <div>
                            <img
                                @click.stop="openEdit(contact)"
                                :src="getEditIcon"
                                class="icon icon-edit"
                                alt="Edit Contact"
                            />
                            <img
                                v-if="contacts.length > 1"
                                @click.stop="removeContact(contact)"
                                :src="getDeleteIcon"
                                class="icon"
                                alt="Remove Contact"
                            />
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
        <bordered-icon-button
            class="add-button"
            :onClick="openAdd"
            :title="$t('contactSelection.table.add')"
            :imgUrl="loadAddIcon"
            :isDisabled="contacts.length > 4"
        />
    </div>
    <contact-sur-site-form
        v-if="contactModal"
        :isPhone="true"
        :contact="contactToEdit"
        @close="closeModal"
        @confirm="confirmContact"
    />
</template>

<script lang="ts">
import RadioBox from '@/components/ui-components/RadioBox.vue';
import { defineComponent } from 'vue';
import { INTERVENTION } from '@/modules/Intervention/store/mutation-types';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import ContactSurSiteForm from './ContactSurSiteForm.vue';
import jwtDecode from 'jwt-decode';
import { ContactSurSiteFormState } from '@/models/ContactSurSiteForm';
import AccountService from '@/modules/Account/services/AccountService';
import Regex from '@/common/utils/Regex';
import ReglePastille from '@/components/regles/ReglePastille.vue';

interface ContactListData {
    contactModal: boolean;
    contactToEdit?: ContactSurSiteFormState;
}

interface UserToken {
    sub: string;
    telephoneMobile: string;
    telephoneFixe: string;
}

export default defineComponent({
    data(): ContactListData {
        return {
            contactModal: false,
            contactToEdit: undefined,
        };
    },
    async mounted() {
        if (this.$store.state.authState.token && this.contacts.length === 0) {
            const { sub, telephoneMobile, telephoneFixe }: UserToken = jwtDecode(this.$store.state.authState.token);
            try {
                const profil = await AccountService.getInformations();
                const profilDTO = profil.data;
                this.addContact({
                    firstname: profilDTO.prenom || '',
                    lastname: profilDTO.nom || '',
                    telephoneMobile: profilDTO.telephonePortable || '',
                    telephoneFixe: profilDTO.telephoneFixe || '',
                    isConnectedUser: true
                });
            } catch (e){
                this.addContact({
                    firstname: sub.split(' ')[0] || '',
                    lastname: sub.split(' ')[1] || '',
                    telephoneMobile: telephoneMobile || '',
                    telephoneFixe: telephoneFixe || '',
                    isConnectedUser: true
                });
            }
        }
    },
    components: {
        RadioBox,
        BorderedIconButton,
        ContactSurSiteForm,
        ReglePastille,
    },
    computed: {
        selectedContact() {
            return this.$store.getters.getInterventionContactSurSiteValue;
        },
        getEditIcon() {
            return require('@/assets/img/edit.svg');
        },
        getDeleteIcon() {
            return require('@/assets/img/delete.svg');
        },
        loadAddIcon() {
            return require('@/assets/img/add-button.svg');
        },
        contacts() {
            return this.$store.getters.getInterventionContactSurSiteListValue;
        },
    },
    methods: {
        closeModal() {
            this.contactToEdit = undefined;
            this.contactModal = false;
        },
        openAdd() {
            this.contactModal = true;
        },
        openEdit(contact: ContactSurSiteFormState) {
            this.contactToEdit = contact;
            this.contactModal = true;
        },
        toggleSelect(contact: ContactSurSiteFormState) {
            // Ensure isConnectedUser is correctly set when selecting a contact
            // We need to preserve the isConnectedUser property as it was set in the mounted() method
            // or in the confirmContact() method
            const updatedContact = { ...contact };
            this.$store.commit(INTERVENTION.SET_CONTACT_SUR_SITE, updatedContact);
        },
        confirmContact(contact: ContactSurSiteFormState) {
            // Preserve isConnectedUser if editing an existing contact
            let isConnected = false;
            if (this.contactToEdit) {
                isConnected = !!this.contactToEdit.isConnectedUser;
                this.removeContact(this.contactToEdit);
            }
            // Set isConnectedUser to the preserved value or false for new contacts
            const updatedContact = { ...contact, isConnectedUser: isConnected };
            this.addContact(updatedContact);
        },
        addContact(contact: ContactSurSiteFormState) {
            this.$store.commit(INTERVENTION.ADD_CONTACT_SUR_SITE, contact);
            this.closeModal();
        },
        removeContact(contact: ContactSurSiteFormState) {
            this.$store.commit(INTERVENTION.REMOVE_CONTACT_SUR_SITE, contact);
        },
        telValidation(value: string) {
            if (!value) return false;
            const reg= Regex.telephone();
            return reg.test(value);
        },
    },
});
</script>

<style scoped>
.item-contact {
    width: 30%;
}
.item-tel-mobile {
    width: 15%;
}
.item-tel-fixe {
    width: 15%;
}
.no-valid {
    border: 1px solid var(--red) !important;
    border-radius: 4px;
}
.contact-list-container {
    display: flex;
    flex-direction: column;
    margin-top: 8px;
}
.contact-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    border-right: 1px solid var(--medium-dark-grey);
    border-left: 1px solid var(--medium-dark-grey);
    border-bottom: 1px solid var(--medium-dark-grey);
    border-radius: 3px;
    text-align: left;
}

.contact-table thead {
    position: sticky;
    top: 0;
    font-size: 14px;
    color: var(--dark-grey);
    font-family: 'Raleway Semi Bold', sans-serif !important;
    font-variant-numeric: lining-nums;
}

.contact-table thead th {
    position: sticky;
    top: 0;
    border-bottom: 1px solid var(--medium-dark-grey);
    border-top: 1px solid var(--medium-dark-grey);
    background-color: white;
    height: 55px;
}

.contact-table thead th:first-child {
    border-top-left-radius: 3px;
    width: 100px;
}

.contact-table thead th:last-child {
    border-top-right-radius: 3px;
}

.contact-table tr td {
    border-bottom: 1px solid var(--medium-dark-grey);
    height: 55px;
}

.contact-table tbody tr:last-child td {
    border: none;
}

.contact-table thead tr:first-child {
    border-top: none;
}

.contact-table tr td {
    box-sizing: border-box;
    padding: 0 5px;
}
.radio {
    margin-left: 8px;
}
.icon {
    height: 24px;
    width: 24px;
    margin: 0;
    cursor: pointer;
}
.icon-edit {
    margin-right: 16px;
}
.buttons-container {
    padding-right: 16px;
}
.buttons-container div {
    display: flex;
    justify-content: flex-end;
}
.add-button {
    margin-top: 8px;
    align-self: flex-end;
}
</style>
